import React, { useEffect, useState, useCallback } from "react";
import { Spin, Alert, Table, Tag } from "antd";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsNewRegistrations } from "../../utils/ApiEndpoints";
import { PageContainer } from "../paUtils";
import styled from "styled-components";

// ── Styled ────────────────────────────────────────────────────────────────

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 18px 24px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f1f5f9;
`;

const TableTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
`;

const TotalBadge = styled.span`
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 10px;
  border-radius: 20px;
`;

const BrandPagesBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.$count > 0 ? "rgba(99,102,241,0.12)" : "#f1f5f9")};
  color: ${(p) => (p.$count > 0 ? "#6366f1" : "#94a3b8")};
`;

const ExpandedWrapper = styled.div`
  padding: 12px 16px 16px;
  background: #f8fafc;
`;

const ExpandedTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
`;

// ── Helpers ───────────────────────────────────────────────────────────────

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const ONBOARDING_COLOR = {
  completed: "green",
  pending: "gold",
  brand_page_pending: "blue",
};

const KYC_COLOR = {
  verified: "green",
  submitted: "blue",
  partial: "orange",
  pending: "gold",
  rejected: "red",
};

const STATUS_COLOR = {
  active: "green",
  inactive: "orange",
  suspended: "red",
};

// ── Brand Pages sub-table columns ─────────────────────────────────────────

const BP_COLUMNS = [
  {
    title: "Brand Page",
    key: "bp",
    width: 200,
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{r.name}</div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>/{r.slug}</div>
      </div>
    ),
  },
  {
    title: "City",
    dataIndex: "city",
    key: "city",
    width: 110,
    render: (v) => v || "—",
  },
  {
    title: "Categories",
    dataIndex: "categories",
    key: "categories",
    render: (v) =>
      Array.isArray(v) && v.length > 0
        ? v
            .slice(0, 3)
            .map((c) => (
              <Tag key={c} style={{ fontSize: 11, marginBottom: 2 }}>
                {c}
              </Tag>
            ))
        : <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 90,
    render: (v) =>
      v ? (
        <Tag color={STATUS_COLOR[v] || "default"} style={{ fontSize: 11 }}>
          {v}
        </Tag>
      ) : "—",
  },
  {
    title: "KYC",
    dataIndex: "kycStatus",
    key: "kycStatus",
    width: 100,
    render: (v) =>
      v ? (
        <Tag color={KYC_COLOR[v] || "default"} style={{ fontSize: 11 }}>
          {v}
        </Tag>
      ) : "—",
  },
  {
    title: "Created",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 110,
    render: fmtDate,
  },
];

// ── Expanded row renderer ─────────────────────────────────────────────────

const ExpandedRow = ({ brandPages }) => (
  <ExpandedWrapper>
    <ExpandedTitle>Brand Pages ({brandPages.length})</ExpandedTitle>
    <Table
      dataSource={brandPages}
      columns={BP_COLUMNS}
      rowKey="brandPageId"
      size="small"
      pagination={false}
      scroll={{ x: 600 }}
    />
  </ExpandedWrapper>
);

// ── Main user columns ─────────────────────────────────────────────────────

const USER_COLUMNS = [
  {
    title: "User",
    key: "user",
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
          {r.name || "—"}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.email}</div>
      </div>
    ),
  },
  {
    title: "Mobile",
    dataIndex: "mobile",
    key: "mobile",
    render: (v) => <span style={{ fontSize: 13 }}>{v || "—"}</span>,
  },
  {
    title: "Account Type",
    dataIndex: "accountType",
    key: "accountType",
    render: (v) =>
      v ? (
        <Tag style={{ fontSize: 11 }}>{v.replace(/_/g, " ")}</Tag>
      ) : "—",
  },
  {
    title: "Onboarding",
    dataIndex: "onboardingStatus",
    key: "onboardingStatus",
    render: (v) =>
      v ? (
        <Tag color={ONBOARDING_COLOR[v] || "default"} style={{ fontSize: 11 }}>
          {v.replace(/_/g, " ")}
        </Tag>
      ) : "—",
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (v) =>
      v ? (
        <Tag color={STATUS_COLOR[v] || "default"} style={{ fontSize: 11 }}>
          {v}
        </Tag>
      ) : "—",
  },
  {
    title: "Signed Up",
    dataIndex: "signupDate",
    key: "signupDate",
    render: fmtDate,
  },
  {
    title: "Last Login",
    dataIndex: "lastLoginAt",
    key: "lastLoginAt",
    render: (v) =>
      v ? (
        fmtDate(v)
      ) : (
        <span style={{ color: "#94a3b8", fontSize: 12 }}>Never</span>
      ),
  },
  {
    title: "Brand Pages",
    key: "bpCount",
    align: "center",
    render: (_, r) => (
      <BrandPagesBadge $count={r.brandPages?.length || 0}>
        {r.brandPages?.length || 0}
      </BrandPagesBadge>
    ),
  },
];

// ── Main Component ────────────────────────────────────────────────────────

function NewRegistrationsTab({ startDate, endDate }) {
  const [data, setData] = useState({ users: [], pagination: {} });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsNewRegistrations, {
        params: { startDate, endDate, page, limit: 20 },
      });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load registrations");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [startDate, endDate]);

  const pagination = data.pagination || {};

  return (
    <PageContainer>
      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 20 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <TableCard>
        <TableHeader>
          <TableTitle>New Registrations</TableTitle>
          <TotalBadge>{pagination.total ?? 0} users</TotalBadge>
        </TableHeader>
        <Spin spinning={loading}>
          <Table
            dataSource={data.users || []}
            columns={USER_COLUMNS}
            rowKey="userId"
            size="middle"
            expandable={{
              expandedRowRender: (record) =>
                record.brandPages?.length > 0 ? (
                  <ExpandedRow brandPages={record.brandPages} />
                ) : (
                  <div style={{ padding: "12px 16px", color: "#94a3b8", fontSize: 13 }}>
                    No brand pages created yet.
                  </div>
                ),
              rowExpandable: () => true,
              expandRowByClick: false,
            }}
            pagination={{
              current: page,
              pageSize: 20,
              total: pagination.total || 0,
              onChange: setPage,
              showSizeChanger: false,
              showTotal: (t) => `${t} users`,
            }}
            scroll={{ x: 820 }}
          />
        </Spin>
      </TableCard>
    </PageContainer>
  );
}

export default NewRegistrationsTab;
